const CODE128_PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312",
  "132212", "221213", "221312", "231212", "112232", "122132", "122231", "113222",
  "123122", "123221", "223211", "221132", "221231", "213212", "223112", "312131",
  "311222", "321122", "321221", "312212", "322112", "322211", "212123", "212321",
  "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121",
  "313121", "211331", "231131", "213113", "213311", "213131", "311123", "311321",
  "331121", "312113", "312311", "332111", "314111", "221411", "431111", "111224",
  "111422", "121124", "121421", "141122", "141221", "112214", "112412", "122114",
  "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112",
  "421211", "212141", "214121", "412121", "111143", "111341", "131141", "114113",
  "114311", "411113", "411311", "113141", "114131", "311141", "411131", "211412",
  "211214", "211232", "2331112",
] as const;

function escapeHtml(value: unknown): string {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

interface Code128BarcodeOptions {
  showValue?: boolean;
}

export function code128BarcodeSvg(
  value: string,
  options: Code128BarcodeOptions = {},
): string {
  const showValue = options.showValue ?? true;
  const normalized = value.normalize("NFKC").trim();
  if (!normalized || [...normalized].some((character) => {
    const code = character.codePointAt(0) ?? 0;
    return code < 32 || code > 126;
  })) {
    return showValue
      ? `<span class="inventory-replenishment-barcode-fallback">${escapeHtml(normalized || "—")}</span>`
      : "";
  }

  const dataCodes = [...normalized].map((character) => (character.codePointAt(0) ?? 32) - 32);
  const checksum = (104 + dataCodes.reduce(
    (sum, code, index) => sum + code * (index + 1),
    0,
  )) % 103;
  const patterns = [104, ...dataCodes, checksum, 106].map((code) => CODE128_PATTERNS[code]!);
  const quietZone = 10;
  const contentWidth = patterns.reduce(
    (total, pattern) => total + [...pattern].reduce((sum, width) => sum + Number(width), 0),
    0,
  );
  let x = quietZone;
  const bars: string[] = [];
  for (const pattern of patterns) {
    [...pattern].forEach((widthText, index) => {
      const width = Number(widthText);
      if (index % 2 === 0) bars.push(`<rect x="${x}" y="0" width="${width}" height="47"/>`);
      x += width;
    });
  }
  const totalWidth = contentWidth + quietZone * 2;
  const valueText = showValue
    ? `<text x="${totalWidth / 2}" y="61" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" fill="#000">${escapeHtml(normalized)}</text>`
    : "";
  return `<svg class="inventory-replenishment-barcode" role="img" aria-label="バーコード ${escapeHtml(normalized)}" viewBox="0 0 ${totalWidth} ${showValue ? 64 : 48}" xmlns="http://www.w3.org/2000/svg">
    <g fill="#000">${bars.join("")}</g>
    ${valueText}
  </svg>`;
}
